/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useState } from "react";
import { useTheme } from "../../theme/ThemeContext";

export const NUMERIC_TYPES = ["integer", "float", "decimal"];
const STRING_TYPES  = ["string", "text"];
const ENUM_TYPES    = ["enum"];
const BOOL_TYPES    = ["boolean"];
export const DATE_TYPES = ["date", "datetime"];

// columns whose types may be compared with each other (UI guardrail)
const COMPARABLE_GROUPS = [
  ["integer", "float", "decimal"],
  ["date", "datetime"],
  ["string", "text", "enum"],
  ["boolean"],
];

function comparableTypes(dataType) {
  const group = COMPARABLE_GROUPS.find(g => g.includes(dataType));
  return group || [dataType];
}

// columns selectable on the RHS of a column-to-column comparison
export function comparableColumns(columns, lhsColumn, lhsType) {
  const allowed = comparableTypes(lhsType);
  return columns.filter(c => c.name !== lhsColumn && allowed.includes(c.data_type));
}

const OPERATORS_BY_TYPE = {
  numeric: [
    { label: "=",       value: "="       },
    { label: "≠",       value: "!="      },
    { label: ">",       value: ">"       },
    { label: "≥",       value: ">="      },
    { label: "<",       value: "<"       },
    { label: "≤",       value: "<="      },
    { label: "BETWEEN", value: "BETWEEN" },
    { label: "IS NULL",     value: "IS NULL"     },
    { label: "IS NOT NULL", value: "IS NOT NULL" },
  ],
  string: [
    { label: "=",        value: "="        },
    { label: "≠",        value: "!="       },
    { label: "LIKE",     value: "LIKE"     },
    { label: "NOT LIKE", value: "NOT LIKE" },
    { label: "~ (regex)",  value: "~"      },
    { label: "!~ (regex)", value: "!~"     },
    { label: "IN",       value: "IN"       },
    { label: "NOT IN",   value: "NOT IN"   },
    { label: "IS NULL",     value: "IS NULL"     },
    { label: "IS NOT NULL", value: "IS NOT NULL" },
  ],
  enum: [
    { label: "=",        value: "="        },
    { label: "≠",        value: "!="       },
    { label: "IN",       value: "IN"       },
    { label: "NOT IN",   value: "NOT IN"   },
    { label: "IS NULL",     value: "IS NULL"     },
    { label: "IS NOT NULL", value: "IS NOT NULL" },
  ],
  boolean: [
    { label: "IS TRUE",     value: "IS TRUE"     },
    { label: "IS FALSE",    value: "IS FALSE"    },
    { label: "IS NULL",     value: "IS NULL"     },
    { label: "IS NOT NULL", value: "IS NOT NULL" },
  ],
  date: [
    { label: "=",       value: "="       },
    { label: "≠",       value: "!="      },
    { label: ">",       value: ">"       },
    { label: "≥",       value: ">="      },
    { label: "<",       value: "<"       },
    { label: "≤",       value: "<="      },
    { label: "BETWEEN", value: "BETWEEN" },
    { label: "IS NULL",     value: "IS NULL"     },
    { label: "IS NOT NULL", value: "IS NOT NULL" },
  ],
  default: [
    { label: "IS NULL",     value: "IS NULL"     },
    { label: "IS NOT NULL", value: "IS NOT NULL" },
  ],
};

export function getOperators(dataType) {
  if (NUMERIC_TYPES.includes(dataType)) return OPERATORS_BY_TYPE.numeric;
  if (STRING_TYPES.includes(dataType))  return OPERATORS_BY_TYPE.string;
  if (ENUM_TYPES.includes(dataType))    return OPERATORS_BY_TYPE.enum;
  if (BOOL_TYPES.includes(dataType))    return OPERATORS_BY_TYPE.boolean;
  if (DATE_TYPES.includes(dataType))    return OPERATORS_BY_TYPE.date;
  return OPERATORS_BY_TYPE.default;
}

const UNARY_OPS = ["IS NULL", "IS NOT NULL", "IS TRUE", "IS FALSE"];
const BETWEEN_OP = "BETWEEN";
const MULTI_OPS  = ["IN", "NOT IN"];

const COMPARISON_OPS = ["=", "!=", ">", ">=", "<", "<="];

export function buildExpression(col, op, val, val2, vals, isNumeric, valKind = "literal") {
  if (!col || !op) return "";
  const q = isNumeric ? (v) => v : (v) => `'${v}'`;
  if (UNARY_OPS.includes(op))  return `"${col}" ${op}`;
  if (op === BETWEEN_OP)       return `"${col}" BETWEEN ${val} AND ${val2}`;
  if (MULTI_OPS.includes(op))  return `"${col}" ${op} (${vals.map(v => q(v)).join(", ")})`;
  if (valKind === "column" && COMPARISON_OPS.includes(op)) {
    if (!val) return "";
    return `"${col}" ${op} "${val}"`;
  }
  return `"${col}" ${op} ${q(val)}`;
}

/**
 * Props:
 *   columns      — array of { name, data_type }
 *   value        — { column, operator, val, val2, vals: [] }
 *   onChange     — (newValue, expressionString) => void
 *   onRemove     — () => void  (undefined = no remove button)
 */
export default function ExpressionRowBuilder({ columns, value, onChange, onRemove }) {
  const { theme } = useTheme();
  const [chipInput, setChipInput] = useState("");

  const col     = value?.column   || "";
  const op      = value?.operator || "";
  const val     = value?.val      || "";
  const val2    = value?.val2     || "";
  const vals    = value?.vals     || [];
  const valKind = value?.valKind  || "literal";

  const colDef    = columns.find(c => c.name === col);
  const dataType  = colDef?.data_type || "";
  const isNumeric = NUMERIC_TYPES.includes(dataType);
  const operators = getOperators(dataType);
  const isDate    = DATE_TYPES.includes(dataType);
  const dateInputType = dataType === "datetime" ? "datetime-local" : "date";
  const canRefColumn  = COMPARISON_OPS.includes(op);
  const rhsColumns    = canRefColumn ? comparableColumns(columns, col, dataType) : [];

  function update(patch) {
    const next = { column: col, operator: op, val, val2, vals, valKind, ...patch };
    const expr = buildExpression(next.column, next.operator, next.val, next.val2, next.vals, isNumeric, next.valKind);
    onChange(next, expr);
  }

  function handleColumnChange(newCol) {
    const newColDef = columns.find(c => c.name === newCol);
    const newOps    = getOperators(newColDef?.data_type || "");
    const firstOp   = newOps[0]?.value || "";
    update({ column: newCol, operator: firstOp, val: "", val2: "", vals: [], valKind: "literal" });
  }

  function addChip() {
    const v = chipInput.trim();
    if (v && !vals.includes(v)) {
      update({ vals: [...vals, v] });
    }
    setChipInput("");
  }

  function removeChip(v) {
    update({ vals: vals.filter(x => x !== v) });
  }

  const rowStyle = {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    flexWrap: "wrap",
    padding: "8px 0",
  };

  const selectStyle = {
    borderRadius: theme.buttonRadius,
    border: `1px solid ${theme.borderColor || "#dee2e6"}`,
    padding: "4px 8px",
    fontSize: 13,
    background: "#fff",
    minWidth: 120,
  };

  const inputStyle = {
    ...selectStyle,
    minWidth: 100,
  };

  return (
    <div style={rowStyle}>
      {/* Column selector */}
      <select
        className="form-select"
        value={col}
        onChange={e => handleColumnChange(e.target.value)}
        style={{ ...selectStyle, minWidth: 140 }}
      >
        <option value="">Column...</option>
        {columns.map(c => (
          <option key={c.name} value={c.name}>{c.name}</option>
        ))}
      </select>

      {/* Operator selector */}
      <select
        className="form-select"
        value={op}
        onChange={e => update({ operator: e.target.value, val: "", val2: "", vals: [], valKind: "literal" })}
        style={selectStyle}
        disabled={!col}
      >
        <option value="">Op...</option>
        {operators.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      {/* Value/Column toggle for comparison operators */}
      {canRefColumn && rhsColumns.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 0, alignSelf: "center" }}>
          <button type="button" onClick={() => update({ valKind: "literal", val: "", val2: "", vals: [] })}
            style={{ ...selectStyle, minWidth: 0, padding: "4px 8px", cursor: "pointer",
              borderTopRightRadius: 0, borderBottomRightRadius: 0,
              background: valKind === "literal" ? theme.primary : "#fff",
              color: valKind === "literal" ? "#fff" : "#555" }}>
            Value
          </button>
          <button type="button" onClick={() => update({ valKind: "column", val: "", val2: "", vals: [] })}
            style={{ ...selectStyle, minWidth: 0, padding: "4px 8px", cursor: "pointer",
              borderTopLeftRadius: 0, borderBottomLeftRadius: 0, borderLeft: "none",
              background: valKind === "column" ? theme.primary : "#fff",
              color: valKind === "column" ? "#fff" : "#555" }}>
            Column
          </button>
        </div>
      )}

      {/* Right-hand column selector */}
      {canRefColumn && valKind === "column" && (
        <select
          className="form-select"
          aria-label="right-hand column"
          value={val}
          onChange={e => update({ val: e.target.value })}
          style={selectStyle}
        >
          <option value="">Column...</option>
          {rhsColumns.map(c => (
            <option key={c.name} value={c.name}>{c.name}</option>
          ))}
        </select>
      )}

      {/* Scalar literal input — context-sensitive */}
      {op && !UNARY_OPS.includes(op) && op !== BETWEEN_OP && !MULTI_OPS.includes(op)
        && !(canRefColumn && valKind === "column") && (
        <input
          style={inputStyle}
          type={isDate ? dateInputType : (isNumeric ? "number" : "text")}
          value={val}
          placeholder="value"
          onChange={e => update({ val: e.target.value })}
        />
      )}

      {op === BETWEEN_OP && (
        <>
          <input style={inputStyle} type={isDate ? dateInputType : "number"} value={val}  placeholder="from" onChange={e => update({ val:  e.target.value })} />
          <span style={{ alignSelf: "center", fontSize: 12, color: "#666" }}>and</span>
          <input style={inputStyle} type={isDate ? dateInputType : "number"} value={val2} placeholder="to"   onChange={e => update({ val2: e.target.value })} />
        </>
      )}

      {MULTI_OPS.includes(op) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, minWidth: 180 }}>
            {vals.map(v => (
              <span key={v} style={{
                background: theme.primary,
                color: "#fff",
                borderRadius: 12,
                padding: "2px 8px",
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}>
                {v}
                <span style={{ cursor: "pointer", fontWeight: 700 }} onClick={() => removeChip(v)}>×</span>
              </span>
            ))}
          </div>
          <div style={{ display: "flex", gap: 4 }}>
            <input
              style={inputStyle}
              value={chipInput}
              placeholder="type and press Enter"
              onChange={e => setChipInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addChip(); } }}
            />
            <button type="button" onClick={addChip}
              style={{ ...selectStyle, background: theme.secondary, color: "#fff", border: "none", cursor: "pointer", minWidth: 40 }}>
              +
            </button>
          </div>
        </div>
      )}

      {/* Expression preview */}
      {col && op && (
        <span style={{ alignSelf: "center", fontSize: 11, color: "#888", fontFamily: "monospace", maxWidth: 200, wordBreak: "break-all" }}>
          {buildExpression(col, op, val, val2, vals, isNumeric, valKind) || "..."}
        </span>
      )}

      {/* Remove button */}
      {onRemove && (
        <button type="button" onClick={onRemove}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#dc3545", fontSize: 18, alignSelf: "center", padding: "0 4px" }}
          title="Remove condition">
          <i className="bi bi-trash" />
        </button>
      )}
    </div>
  );
}
